'use client';

import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import { useTrackingRegistry } from 'context/TrackingContext';
import type { PropsWithClassName } from 'types/react-props';

type Milestone = 0.5 | 1;

type ScrollTrackWrapperProps = PropsWithClassName<{
  milestones: Array<Milestone>; // e.g. [0.5, 1]
  eventName: string;
  children: ReactNode;
}>;

type ScrollEventPush = {
  event: string;
  scroll_depth: string;
  read_more_button_activated?: boolean | 'deactivated';
};

const dataLayerPush = (event: ScrollEventPush) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
};

export const ScrollTrackWrapper = ({
  milestones = [0.5, 1],
  eventName,
  children,
  className,
}: ScrollTrackWrapperProps) => {
  const { has } = useTrackingRegistry();

  const rootRef = useRef<HTMLDivElement | null>(null);

  const sortedMilestones = useMemo(
    () =>
      [...milestones]
        .map(Number)
        .filter((number) => number > 0 && number <= 1)
        .sort((numberA, numberB) => numberA - numberB),
    [milestones]
  );

  useEffect(() => {
    const element = rootRef.current;
    if (!element || sortedMilestones.length === 0) return;

    const prevPosition = element.style.position;
    if (!prevPosition || prevPosition === 'static') element.style.position = 'relative';

    const sentNormal = new Set<number>();
    const sentAfter = new Set<number>();

    const markers = sortedMilestones.map((milestone) => {
      const div = document.createElement('div');
      div.setAttribute('data-cy', `scroll-track-${milestone}`);
      div.style.position = 'absolute';
      div.style.left = '0';
      div.style.width = '1px';
      div.style.height = '1px';
      div.style.pointerEvents = 'none';
      div.style.top = '0px';
      element.appendChild(div);
      return { milestone, node: div };
    });

    const place = () => {
      const height = element.scrollHeight || element.getBoundingClientRect().height;
      for (const { milestone, node } of markers) {
        const yPosition = Math.max(0, Math.floor(height * milestone) - 1);
        node.style.top = `${yPosition}px`;
      }
    };

    place();

    const resizeObserver = new ResizeObserver(place);
    resizeObserver.observe(element);

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const hit = markers.find((marker) => marker.node === entry.target);
          if (!hit) continue;

          const hasReadMore = has('readMore');
          const hasReadMoreClicked = has('readMoreClicked');

          if (!sentNormal.has(hit.milestone)) {
            sentNormal.add(hit.milestone);
            dataLayerPush({
              event: eventName,
              scroll_depth: String(hit.milestone),
              read_more_button_activated: hasReadMore,
            });
          }

          if (hasReadMoreClicked && !sentAfter.has(hit.milestone)) {
            sentAfter.add(hit.milestone);
            dataLayerPush({
              event: eventName,
              scroll_depth: String(hit.milestone),
              read_more_button_activated: 'deactivated',
            });
          }
        }
      },
      { threshold: 0 }
    );

    for (const { node } of markers) intersectionObserver.observe(node);

    return () => {
      intersectionObserver.disconnect();
      resizeObserver.disconnect();
      for (const { node } of markers) node.remove();
    };
  }, [sortedMilestones, eventName, has]);

  return (
    <div ref={rootRef} className={className}>
      {children}
    </div>
  );
};
